import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

import history from '../history';
import { sanitizeRedirectPath } from './safeRedirect';

export const DEEP_LINK_HOSTS = ['app.arb.systems'];

const DUPLICATE_WINDOW_MS = 3000;

let listening = false;
let lastHandled: { url: string; at: number } | null = null;

const parseAppLink = (url?: string | null): URL | null => {
  if (typeof url !== 'string' || !url) return null;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch (e) {
    return null;
  }

  if (parsed.protocol !== 'https:' || parsed.port) return null;
  if (!DEEP_LINK_HOSTS.includes(parsed.hostname.toLowerCase())) return null;
  return parsed;
};

const isStaticPage = (parsed: URL): boolean => parsed.pathname.toLowerCase().endsWith('.html');

export const resolveDeepLinkRoute = (url?: string | null): string | null => {
  const parsed = parseAppLink(url);
  if (!parsed || isStaticPage(parsed)) return null;
  const pathname = parsed.pathname.replace(/^\/{2,}/, '/');
  return sanitizeRedirectPath(`${pathname}${parsed.search}${parsed.hash}`);
};

export const resolveStaticPageUrl = (url?: string | null): string | null => {
  const parsed = parseAppLink(url);
  if (!parsed || !isStaticPage(parsed)) return null;
  return `${parsed.origin}${parsed.pathname}${parsed.search}${parsed.hash}`;
};

const isDuplicate = (url: string): boolean => {
  const now = Date.now();
  const duplicate = lastHandled?.url === url && now - lastHandled.at < DUPLICATE_WINDOW_MS;
  lastHandled = { url, at: now };
  return duplicate;
};

export const handleDeepLink = (url?: string | null): void => {
  if (!url || isDuplicate(url)) return;

  const staticPage = resolveStaticPageUrl(url);
  if (staticPage) {
    window.location.assign(staticPage);
    return;
  }

  const route = resolveDeepLinkRoute(url);
  if (route) {
    history.push(route);
  }
};

export const initDeepLinks = async (): Promise<void> => {
  if (listening) return;
  if (!Capacitor.isNativePlatform() || !Capacitor.isPluginAvailable('App')) return;
  listening = true;

  try {
    await App.addListener('appUrlOpen', (event) => handleDeepLink(event?.url));
    const launch = await App.getLaunchUrl();
    handleDeepLink(launch?.url);
  } catch (e) {
    listening = false;
  }
};
